import * as fromPaymentConfiguration from './payment-configuration.reducer';
import * as fromPaymentProcess from './payment-process.reducer';
import * as fromPaymentLayoutCustomers from './payment-layout-customers.reducer';
import * as fromPaymentLayoutCompanies from './payment-layout-companies.reducer';
import * as fromPaymentLayoutJobsites from './payment-layout-jobsites.reducer';
import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';
import { PaymentDocument } from './../../../components/bulk-payment/models/payment-document.model';
import { PaymentDetail } from './../../models/payment-detail';
import { Customer } from './../../models/customer';
import { Company } from './../../models/company';
import { Jobsite } from './../../models/jobsite';

export interface State {
    paymentConfiguration: fromPaymentConfiguration.State;
    paymentProcess: fromPaymentProcess.State;
    customers: fromPaymentLayoutCustomers.State;
    companies: fromPaymentLayoutCompanies.State;
    jobsites: fromPaymentLayoutJobsites.State;
}

export const reducers: ActionReducerMap<State> = {
    paymentConfiguration: fromPaymentConfiguration.reducer,
    paymentProcess: fromPaymentProcess.reducer,
    customers: fromPaymentLayoutCustomers.reducer,
    companies: fromPaymentLayoutCompanies.reducer,
    jobsites: fromPaymentLayoutJobsites.reducer
};

export const paymentsState = createFeatureSelector<State>('payments');
const paymentConfigurationState = createSelector(paymentsState, (state) => state && state.paymentConfiguration);
const paymentProcessState = createSelector(paymentsState, (state) => state && state.paymentProcess);
const customersState = createSelector(paymentsState, (state) => state && state.customers);
const companiesState = createSelector(paymentsState, (state) => state && state.companies);
const jobsitesState = createSelector(paymentsState, (state) => state && state.jobsites);
export const getPaymentExternalData = createSelector(paymentProcessState, fromPaymentProcess.getPaymentExternalData);
export const getReferenceCode = createSelector(paymentProcessState, fromPaymentProcess.getReferenceCode);
export const getPayerInformation = createSelector(paymentProcessState, fromPaymentProcess.getPayerInformation);
export const getPaymentHeader = createSelector(paymentProcessState, fromPaymentProcess.getPaymentHeader);
export const isProcessingReference = createSelector(paymentProcessState, fromPaymentProcess.isProcessingReference);
export const isProcessingNetwork = createSelector(paymentProcessState, fromPaymentProcess.isProcessingNetwork);
export const getSalesOrderNumber = createSelector(paymentProcessState, fromPaymentProcess.getSalesOrderNumber);

export const getPaymentConfiguration = createSelector(paymentConfigurationState, fromPaymentConfiguration.getPaymentConfiguration);

export const getPaymentDocuments = createSelector(paymentProcessState, (state) => {
    let documents: PaymentDocument[] = [];
    if (state && state.entities) {
        documents = Object.keys(state.entities).map(i => state.entities[i]);
    }

    return documents;
});

export const isReadyToDisplay = createSelector(getPaymentConfiguration, getPaymentDocuments,
    (state, documents) => {
    return state && state.paymentMethods.length > 0 && documents !== undefined;
});

export const getPaymentDetails = createSelector(getPaymentDocuments, (state) => {
    const details: PaymentDetail[] = [];

    state.forEach(document => {
        details.push({
            // accountingDocNo?: string;
            amountLocalCurrency: document.payment ? document.payment.toFixed(2) : '0',
            // assignmentNo?: string;
            // bloquedXYes?: string;
            // client?: string;
            companyCode: document.companyCode,
            countryCompany: document.countryCode.trim(),
            customerNo1: document.customerCode,
            debitFlag: document.debitFlag,
            // discountIndicator: document.expectedPayment === document.payment && document.discount > 0 ? 'X' : '', // change
            // documentClass: document.documentTypeCode,
            documentID: document.documentId,
            // documentNumberOfTheClearingDocument: '', // Borrar
            fiscalYear: document.accountingFiscalYear,
            idTransaction: document.companyCode,
            // name1?: string;
            // name133704237?: string;
            documentCode: document.documentCode,
            numberDocumentPaid: document.documentCode,
            paymentFlagPartialPayment: document.expectedPayment > document.payment ? 'X' : '',
            reasonCodeForPayments: document.reasonCode ? document.reasonCode : '',
            referenceCode: document.referenceNumber,
            shitpTo: document.jobSiteCode,
            shortKey4HouseBank: document.documentItem
        });
    });

    return details;
});

export const getDocumentsCount = createSelector(getPaymentDocuments, (state) => {
    return state.length;
});

export const getDocumentsTotalToPay = createSelector(getPaymentDocuments, (state) => {
    let totalToPay = 0;

    state.forEach(document => {
        totalToPay += document.payment;
    });

    return totalToPay;
});

export const getCustomers = createSelector(customersState, (state) => {
    let customers: Customer[] = [];
    if (state && state.entities) {
        customers = Object.keys(state.entities).map(i => state.entities[i]);
    }

    return customers;
});

export const getCompanies = createSelector(companiesState, (state) => {
    let companies: Company[] = [];
    if (state && state.entities) {
        companies = Object.keys(state.entities).map(i => state.entities[i]);
        companies = companies.filter(company => company.paymentAllowed === 1);
    }
    return companies;
});

export const getJobsites = createSelector(jobsitesState, (state) => {
    let jobsites: Jobsite[] = [];
    if (state && state.entities) {
        jobsites = Object.keys(state.entities).map(i => state.entities[i]);
    }

    return jobsites;
});

export const getSelectedCustomer = createSelector(paymentProcessState, getCustomers, (state, customers) => {
    return state && state.customer && customers.find(customer => customer.customerCode === state.customer);
});

export const getSelectedCompany = createSelector(paymentProcessState, getCompanies, (state, companies) => {
    return state && state.company && companies.find(company => company.companyCode === state.company);
});

export const getSelectedJobsite = createSelector(paymentProcessState, getJobsites, (state, jobsites) => {
    return jobsites.find(jobsite => jobsite.jobsiteCode === state.jobsite);
});

export const getSelectedPaymentMethod = createSelector(paymentProcessState, getPaymentConfiguration, (state, paymentConfiguration) => {
    if (paymentConfiguration) {
        return paymentConfiguration.paymentMethods.find(paymentMethod => paymentMethod.code === state.paymentMethod);
    }
});

export const getSelectedNetworking = createSelector(paymentProcessState, getSelectedPaymentMethod, (state, paymentMethod) => {
    if (paymentMethod) {
        return paymentMethod.paymentNetworks.find(networking => networking.bankCode === state.networking);
    }
});

export const getSelectedNetworkingDetail = createSelector(paymentProcessState, getSelectedNetworking, (state, networking) => {
    if (networking) {
        return networking.details.find(detail => detail.codeDetail === state.networkingDetail);
    }
});

export const isReadyToCallNetwork = createSelector(paymentProcessState, (state) => {
    return state.referenceCode !== undefined && state.payerInfo !== undefined;
});

export const getTotalToPay = createSelector(paymentProcessState, (state) => {
    return state.paymentHeader === undefined ? '0.00' : parseFloat(state.paymentHeader.amountLocalCurrency).toFixed(2) ;
});

export const isAdvancePayment = createSelector(paymentProcessState, getDocumentsCount, (state, documentsCount) => {
    return state.paymentHeader !== undefined && documentsCount === 0;
});
